import {
    faArrowUpRightFromSquare,
    faClipboardList,
    faClockRotateLeft,
    faCloud,
    faDatabase,
    faFileCirclePlus,
    faFileLines,
    faUpload,
} from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
    erpToolProviderTranslationKey,
    isErpToolProvider,
    MASTER_DATA_TOOL_PROVIDER_KEY,
    ORDER_HISTORY_TOOL_PROVIDER_KEY,
} from './config/erpToolProviders.ts';
import { withIcon } from '../../../core/components/Icon.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import { canSeeSupportUserConfigs } from '../../../users/utils.ts';

const DatabaseIcon = withIcon(faDatabase);
const HistoryIcon = withIcon(faClockRotateLeft);
const DocumentCreationIcon = withIcon(faFileCirclePlus);
const FileIcon = withIcon(faFileLines);
const WebsiteIcon = withIcon(faArrowUpRightFromSquare);
const ExpertKnowledgeIcon = withIcon(faClipboardList);
const SharePointIcon = withIcon(faCloud);
const SftpIcon = withIcon(faUpload);

interface CapabilityItem {
    key: string;
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description?: string;
}

const CapabilityList = ({ label, items }: { label: string; items: CapabilityItem[] }) => (
    <div className="flex flex-col gap-5">
        <span className="text-xs font-medium text-content-medium">{label}</span>
        <ul className="m-0 grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2">
            {items.map(({ key, icon: Icon, title, description }) => (
                <li key={key} className="flex items-start gap-4">
                    <Icon className="mt-0.5 flex-none w-5 self-center text-sm text-content-medium" />
                    <span className="flex flex-col">
                        <span className="text-sm font-medium text-content-strong">{title}</span>
                        {description && <span className="text-xs text-content-medium">{description}</span>}
                    </span>
                </li>
            ))}
        </ul>
    </div>
);

// Read-only overview of what a new inbox assistant can work with; everything is configured later on its
// config page. Knowledge sources follow the config page (AgentToolProvidersFormFields.tsx): SharePoint is
// always offered, SFTP only to support users.
const AssistantCapabilitiesOverview = ({
    availableToolProviders,
}: {
    availableToolProviders: { key: string; name: string }[];
}) => {
    const { t } = useTranslation('assistants');
    const { t: tAgentConfig } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const { user } = useApplicationContext();

    // same rules as the config page (AgentToolProvidersFormFields.tsx): ERPs as offered by the backend,
    // master data and document creation always, order history only when available
    const integrations: CapabilityItem[] = [
        ...availableToolProviders
            .filter((provider) => isErpToolProvider(provider.key))
            .map((provider) => ({
                key: provider.key,
                icon: DatabaseIcon,
                title: tAgentConfig(`toolProviders.${erpToolProviderTranslationKey(provider.key)}.name`, {
                    defaultValue: provider.name,
                }),
                description: t('wizard.capabilities.erp'),
            })),
        {
            key: MASTER_DATA_TOOL_PROVIDER_KEY,
            icon: DatabaseIcon,
            title: t('wizard.capabilities.masterData.title'),
            description: t('wizard.capabilities.masterData.description'),
        },
        ...(availableToolProviders.some((provider) => provider.key === ORDER_HISTORY_TOOL_PROVIDER_KEY)
            ? [
                  {
                      key: ORDER_HISTORY_TOOL_PROVIDER_KEY,
                      icon: HistoryIcon,
                      title: t('wizard.capabilities.orderHistory.title'),
                      description: t('wizard.capabilities.orderHistory.description'),
                  },
              ]
            : []),
        {
            key: 'documentCreation',
            icon: DocumentCreationIcon,
            title: t('wizard.capabilities.documentCreation.title'),
            description: t('wizard.capabilities.documentCreation.description'),
        },
    ];

    const knowledge: CapabilityItem[] = [
        { key: 'files', icon: FileIcon },
        { key: 'websites', icon: WebsiteIcon },
        { key: 'expertKnowledge', icon: ExpertKnowledgeIcon },
        { key: 'sharepoint', icon: SharePointIcon },
        ...(canSeeSupportUserConfigs(user) ? [{ key: 'sftp', icon: SftpIcon }] : []),
    ].map(({ key, icon }) => ({
        key,
        icon,
        title: t(`wizard.capabilities.${key}.title`),
        description: t(`wizard.capabilities.${key}.description`),
    }));

    return (
        <div className="flex flex-col gap-6">
            <CapabilityList label={t('wizard.capabilities.integrations')} items={integrations} />
            <CapabilityList label={t('wizard.capabilities.knowledge')} items={knowledge} />
        </div>
    );
};

export default AssistantCapabilitiesOverview;
